import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Result, Row, Space } from 'antd';

import { AdvancedTechnologyEdaVendorWithChildren } from '@app/domain-maps-interfaces';
import { ConfirmDelete, OptionCard, Theme } from '@app/web-components';

import { useDeleteEdaVendor, useEditEdaVendor } from '../data';

import { EdaVendorFormData, useVendorMapForm } from './use-vendor-map-form';

export type EditFormProps = {
  record: AdvancedTechnologyEdaVendorWithChildren;
};

export const EditForm = ({ record }: EditFormProps) => {
  const [open, setOpen] = useState(false);
  const del = useDeleteEdaVendor();
  const edit = useEditEdaVendor(record.id);
  const { token } = Theme.useToken();
  const navigate = useNavigate();
  const {
    getCancelButtonProps,
    getEDAVendorOptionCardProps,
    getErrorFormItemProps,
    getFormProps,
    getProcessesFormItemProps,
    getResetButtonProps,
    getSaveButtonProps,
    getSuccessResultProps,
    getSupplierFormItemProps,
    getT3RequiredOptionCardProps,
  } = useVendorMapForm(edit);

  const initialValues: EdaVendorFormData = {
    supplier: { label: record.supplier.name, value: record.supplierId.toString() },
    advancedTechnologyEdaVendorProcesses: record.advancedTechnologyEdaVendorProcesses.map(({ process }) => ({
      label: process.name,
      value: process.id.toString(),
    })),
    edaVendor: record.edaVendor,
    t3Required: record.t3Required,
  };

  if (edit.isSuccess) {
    return <Result {...getSuccessResultProps()} title="Successfully edited EDA or CW Vendor mapping" />;
  }

  return (
    <Space size="large" style={{ width: '100%' }} wrap>
      <ConfirmDelete.Modal
        itemId={open ? record.id : undefined}
        itemName={record.supplier.name}
        onClose={() => setOpen(false)}
        onOk={() => del.mutate(record.id, { onSuccess: () => navigate('../') })}
      />
      <Form {...getFormProps()} initialValues={initialValues}>
        {edit.error ? <Form.Item {...getErrorFormItemProps()} /> : undefined}
        <Card>
          <Form.Item {...getSupplierFormItemProps()}>
            <span>{initialValues.supplier.label}</span>
          </Form.Item>
          <Form.Item {...getProcessesFormItemProps()} />
          <Row gutter={20} style={{ marginBottom: 30 }}>
            <Col span={6}>
              <OptionCard {...getEDAVendorOptionCardProps()} />
            </Col>
            <Col span={6}>
              <OptionCard {...getT3RequiredOptionCardProps()} />
            </Col>
          </Row>
          <Space style={{ marginTop: 20 }}>
            <Button {...getSaveButtonProps()} />
            <Button {...getResetButtonProps()} />
            <Button icon={<DeleteOutlined style={{ color: token.red7 }} />} onClick={(e) => setOpen(true)}>
              Delete
            </Button>
            <Button {...getCancelButtonProps()} />
          </Space>
        </Card>
      </Form>
    </Space>
  );
};
